import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { LogoFull } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { SignInButton } from "@/components/UserMenu";

export const STATUS_STYLES = {
  placed: "bg-saffron-subtle text-saffron-dark",
  confirmed: "bg-ragi/10 text-ragi",
  preparing: "bg-terracotta/10 text-terracotta",
  out_for_delivery: "bg-sky-100 text-sky-800",
  delivered: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-stone-200 text-stone-600",
};

export const StatusPill = ({ status, testId }) => (
  <span className={`rounded-full px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] ${STATUS_STYLES[status] || STATUS_STYLES.placed}`} data-testid={testId}>{status.replaceAll("_", " ")}</span>
);

export const PAY_LABEL = { pending: "payment pending", verification_pending: "UPI · verifying", paid: "paid", cod: "pay on delivery", failed: "payment failed" };

export const fmtDate = (iso) => new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });

export default function AccountShell({ title, eyebrow, children, requireAdmin = false, testId, signInMessage = "Sign in with Google to see your orders and saved details." }) {
  const { user, loading } = useAuth();
  const blocked = !loading && (!user || (requireAdmin && !user.is_admin));

  return (
    <div className="grain min-h-screen bg-cream font-body text-ragi-dark" data-testid={testId}>
      <header className="border-b border-cream-muted">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 py-5 sm:px-8">
          <Link to="/" aria-label="Kandula Home Foods home" data-testid="account-brand-link"><LogoFull /></Link>
          <Link to="/#catalog" className="inline-flex items-center gap-2 text-sm font-semibold text-ragi-dark hover:text-terracotta" data-testid="account-back-to-shop"><ArrowLeft className="h-4 w-4" /> Back to shop</Link>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 pb-24 pt-12 sm:px-8">
        <p className="mb-3 font-mono text-xs uppercase tracking-[0.2em] text-terracotta">{eyebrow}</p>
        <h1 className="font-display text-4xl font-semibold sm:text-5xl" data-testid="account-page-title">{title}</h1>
        {loading ? (
          <p className="mt-10 text-sm text-muted-foreground" data-testid="account-loading">Loading…</p>
        ) : blocked ? (
          <div className="mt-10 max-w-md rounded-3xl border border-cream-muted bg-cream-secondary p-8" data-testid="account-signin-required">
            <p className="font-display text-2xl">{user ? "Owner access only" : "Please sign in"}</p>
            <p className="mt-2 text-sm text-muted-foreground" data-testid="account-signin-message">{user ? "This area is reserved for the Kandula Home Foods owner account." : signInMessage}</p>
            {!user && <SignInButton className="mt-5" label="Sign in with Google" testId="account-google-signin" />}
          </div>
        ) : (
          <div className="mt-10">{children}</div>
        )}
      </main>
    </div>
  );
}
